import { createContext, useContext } from 'react';

import type { ChartContextValue, SeriesDisplayMetadata } from '../../types/ChartContext';

/**
 * Lightweight context for sharing cross-cutting chart configuration.
 * Used by both LineChart and ColumnChart to coordinate child components.
 * Only used for things that multiple components need to coordinate on.
 */
export const ChartContext = createContext<ChartContextValue | null>(null);

/**
 * Hook to access the chart context.
 * Works with both LineChart and ColumnChart.
 *
 * @returns The chart context value
 * @throws Error if used outside of a chart component
 */
export function useChartContext(): ChartContextValue {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error('Chart components must be used within a <LineChart> or <ColumnChart> component');
  }
  return context;
}

// Re-export SeriesDisplayMetadata for convenience
export type { SeriesDisplayMetadata };
